import { useEffect, useMemo, useRef, useState } from "react";
import { createGenericContext } from "@portal/app/_common/lib/createGenericContext";
import { gql } from "graphql-tag";

void gql`
  fragment FlowStateUpdateFragment on Customer {
    id
    programFlow(programId: $programId) @include(if: $programFlow) {
      ...ProgramFlowFragment
    }
    questionnaireFlow @include(if: $questionnaireFlow) {
      ...QuestionnaireFlowFragment
    }
    skipperFlow(flowIntent: $flowIntent) @include(if: $skipperFlow) {
      ...SkipperFlowFragment
    }
    signInFlow @include(if: $signInFlow) {
      ...SignInFlowFragment
    }
  }
  fragment SkipperFlowFragment on SkipperFlowState {
    id
    initialStep {
      id
      name
      status
    }
    steps {
      id
      name
      status
      isBlocking
    }
    hasBlockingStep
  }
`;

type FlowStepNameHelper<
  FlowName extends "programFlow" | "questionnaireFlow" | "skipperFlow" | "signInFlow" | undefined,
> = FlowName extends "programFlow"
  ? GQL.ProgramFlowStepName
  : FlowName extends "questionnaireFlow"
    ? GQL.QuestionnaireFlowStepName
    : FlowName extends "skipperFlow"
      ? GQL.SkipperFlowStepName
      : FlowName extends "signInFlow"
        ? GQL.SignInFlowStepName
        :
            | GQL.ProgramFlowStepName
            | GQL.QuestionnaireFlowStepName
            | GQL.SkipperFlowStepName
            | GQL.SignInFlowStepName;

export type FlowContextProps<
  FlowName extends
    | "programFlow"
    | "questionnaireFlow"
    | "skipperFlow"
    | "signInFlow"
    | undefined = undefined,
  FlowStepName = FlowStepNameHelper<FlowName>,
> = {
  flow: FlowName;
  data: {
    programId?: string;
    flowIntent?: GQL.FlowIntent;
    complianceExclusionTags?: string[];
    excludedProgramIds?: string[];
  };
  getFlowArgs: () => {
    programFlow: boolean;
    questionnaireFlow: boolean;
    skipperFlow: boolean;
    signInFlow: boolean;
    programId?: string;
    flowIntent?: GQL.FlowIntent;
  };
  goBack: (() => void) | null;
  goForward: () => void;
  /**
   * If a callbackStep is provided, the flow will go to that step after the
   * step is completed.
   * @param stepName
   * @param callbackStep
   */
  goToStep: (stepName: FlowStepName, callbackStep?: FlowStepName) => void;
  stepShown: FlowStepName;
};

const [useFlowContext, FlowContext] = createGenericContext<FlowContextProps>("FlowContext");

export { useFlowContext };

export const FlowContextProvider = <
  FlowName extends "programFlow" | "questionnaireFlow" | "skipperFlow" | "signInFlow",
  FlowStepName = FlowStepNameHelper<FlowName>,
>({
  flow,
  programId,
  flowIntent,
  complianceExclusionTags,
  excludedProgramIds,
  flowState,
  children,
}: {
  flow: FlowName;
  complianceExclusionTags?: string[];
  excludedProgramIds?: string[];
  flowState: {
    initialStep: { name: FlowStepName };
    hasBlockingStep: boolean;
    steps: {
      name: FlowStepName;
      status: GQL.FlowStepStatus;
    }[];
  };
  children:
    | NonNullable<React.ReactNode>
    | ((value: FlowContextProps<FlowName, FlowStepName>) => React.ReactNode);
} & (FlowName extends "programFlow"
  ? {
      programId: string;
      flowIntent?: never;
    }
  : FlowName extends "questionnaireFlow"
    ? {
        flowIntent?: never;
        programId?: never;
      }
    : FlowName extends "skipperFlow"
      ? {
          flowIntent: GQL.FlowIntent;
          programId?: never;
        }
      : FlowName extends "signInFlow"
        ? {
            flowIntent?: never;
            programId?: never;
          }
        : never)) => {
  const [stepShown, setStepShown] = useState<FlowStepName>(() => flowState.initialStep.name);
  const callbackStepRef = useRef<FlowStepName | undefined>(undefined);
  const isRedoingSteps = useRef(false);

  const previousStep = useMemo(() => {
    if (!stepShown) {
      return undefined;
    }
    const stepNames = flowState.steps
      .filter(({ status }) => status !== "UNAVAILABLE")
      .map((step) => step.name);
    return stepNames[stepNames.indexOf(stepShown) - 1];
  }, [flowState.steps, stepShown]);

  const context: FlowContextProps<FlowName, FlowStepName> = useMemo(
    () => ({
      flow,
      data: {
        programId,
        flowIntent,
        complianceExclusionTags,
        excludedProgramIds,
      },
      stepShown: stepShown as FlowStepName,
      goForward: () => {
        if (callbackStepRef.current) {
          setStepShown(callbackStepRef.current);
          callbackStepRef.current = undefined;
        }
        if (!isRedoingSteps.current) {
          // The useEffect will handle this when the flowState changes
          return;
        }
        if (isRedoingSteps.current && flowState.hasBlockingStep) {
          return setStepShown(flowState.initialStep.name);
        }
        setStepShown((currentStep) => {
          const stepNames = flowState.steps
            .filter(({ status }) => status !== "UNAVAILABLE")
            .map((step) => step.name);
          const indexOfCurrentStep = stepNames.indexOf(currentStep);
          if (indexOfCurrentStep === -1) {
            return flowState?.initialStep.name;
          }
          const nextStep = stepNames[indexOfCurrentStep + 1] ?? flowState?.initialStep.name;
          if (flowState.initialStep.name === nextStep) {
            isRedoingSteps.current = false;
          }
          return nextStep;
        });
      },
      goBack:
        stepShown !== previousStep && !!previousStep
          ? () => {
              isRedoingSteps.current = true;
              setStepShown(previousStep);
            }
          : null,
      goToStep: (stepName: FlowStepName, callbackStep?: FlowStepName) => {
        isRedoingSteps.current = true;
        callbackStepRef.current = callbackStep;
        setStepShown(stepName);
      },
      getFlowArgs: () => ({
        programFlow: flow === "programFlow",
        questionnaireFlow: flow === "questionnaireFlow",
        skipperFlow: flow === "skipperFlow",
        signInFlow: flow === "signInFlow",
        programId,
        flowIntent,
      }),
    }),
    [
      flow,
      complianceExclusionTags,
      excludedProgramIds,
      flowIntent,
      flowState.hasBlockingStep,
      flowState.initialStep.name,
      flowState.steps,
      previousStep,
      programId,
      stepShown,
    ]
  );

  useEffect(() => {
    if (!isRedoingSteps.current && stepShown !== flowState.initialStep.name) {
      setStepShown(flowState.initialStep.name);
    } else if (flowState.initialStep.name === stepShown) {
      isRedoingSteps.current = false;
    }
  }, [flowState.initialStep.name, flowState.steps, stepShown]);

  return (
    <FlowContext.Provider value={context as unknown as FlowContextProps}>
      {typeof children === "function"
        ? children(context as unknown as FlowContextProps<FlowName, FlowStepName>)
        : children}
    </FlowContext.Provider>
  );
};
